import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import LotusLoader from "../components/LotusLoader";
import Particles from "../components/Particles";
import { ordersApi } from "../lib/api";
import { BRAND } from "../lib/constants";
import { titleCaseName } from "../lib/text";

export default function OrderConfirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersApi
      .get(id)
      .then((o) => {
        // Edge case: paid but no address submitted yet → bounce to address page
        if (o.paymentStatus === "Payment Confirmed" && !o.deliveryAddress) {
          navigate(`/order/${id}/address`, { replace: true });
          return;
        }
        setOrder(o);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <LotusLoader context="general" />;
  if (!order) {
    return (
      <div className="min-h-screen">
        <BrandHeader />
        <div className="max-w-md mx-auto px-5 py-20 text-center">
          <h1 className="font-serif text-2xl text-[#2C0A5E]">Order not found</h1>
        </div>
      </div>
    );
  }

  const addr = order.deliveryAddress || {};
  const firstName = titleCaseName((order.fullName || "").trim().split(/\s+/)[0]) || "Beautiful Soul";
  const paymentId = order.razorpay_payment_id || "";
  const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(`Namaste, this is regarding order #${order.orderNumber}.`)}`;

  return (
    <div className="min-h-screen" data-testid="order-confirmation-page">
      <BrandHeader />

      {/* Hero block with mystical violet — activated-prescription success */}
      <section className="relative overflow-hidden hero-mystical">
        <Particles variant="violet" />
        <div className="relative max-w-3xl mx-auto px-5 md:px-10 py-14 md:py-20 text-center">
          <div className="ph-overline" style={{ color: "#D4A855" }}>Prescription Activated</div>
          <h1 className="font-serif text-3xl md:text-5xl mt-3 text-white" data-testid="oc-headline">
            <span aria-hidden>&#127807;</span> Your prescription is activated, {firstName}!
          </h1>
          <p className="font-serif italic text-lg md:text-xl mt-5 max-w-xl mx-auto" style={{ color: "#FAF7F2" }} data-testid="oc-tagline">
            Your bracelet enters handcrafting today. Ships in 5&ndash;7 days.
          </p>

          <div className="mt-6 grid gap-1.5 text-xs md:text-sm max-w-md mx-auto" data-testid="oc-order-ids">
            <div className="inline-flex flex-wrap items-center justify-center gap-2">
              <span className="tracking-[0.3em] uppercase" style={{ color: "#D4A855" }}>Order</span>
              <span className="font-mono text-white" data-testid="oc-order-number">#{order.orderNumber}</span>
            </div>
            {paymentId && (
              <div className="inline-flex flex-wrap items-center justify-center gap-2">
                <span className="tracking-[0.3em] uppercase" style={{ color: "#D4A855" }}>Payment</span>
                <span className="font-mono text-white" data-testid="oc-payment-id">{paymentId}</span>
              </div>
            )}
          </div>

          <div className="mt-8">
            <Link
              to={`/my-prescription`}
              className="ph-btn-primary"
              data-testid="oc-view-prescription"
            >
              View My Full Prescription &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-[#FAF7F2] py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-5 md:px-10 grid gap-5 md:grid-cols-2">
          {/* Delivery summary */}
          <div className="ph-card p-6" data-testid="oc-address-summary">
            <div className="ph-overline">Delivering To</div>
            <div className="mt-3 text-[#2C0A5E] text-sm leading-relaxed">
              <div className="font-medium">{addr.fullName}</div>
              <div>{addr.line1}</div>
              {addr.line2 && <div>{addr.line2}</div>}
              {addr.landmark && <div className="text-[#6B5E7E]">Landmark: {addr.landmark}</div>}
              <div>{addr.city}, {addr.state} {addr.pincode}</div>
              <div className="mt-2 text-[#6B5E7E]">Mobile: +91 {addr.mobile}</div>
            </div>
            <Link
              to={`/order/${id}/address`}
              className="inline-block mt-4 text-xs tracking-[0.2em] uppercase"
              style={{ color: "#6A0DAD" }}
              data-testid="oc-edit-address"
            >
              Edit address &rarr;
            </Link>
          </div>

          {/* Next steps */}
          <div className="ph-card p-6" data-testid="oc-next-steps">
            <div className="ph-overline">Next steps</div>
            <ul className="mt-3 space-y-3 text-sm text-[#2C0A5E]">
              <li className="flex gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium" style={{ background: "#EEEDFE", color: "#6A0DAD" }}>1</span>
                <span><strong>Handcrafting today</strong> &mdash; your bracelet enters production.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium" style={{ background: "#EEEDFE", color: "#6A0DAD" }}>2</span>
                <span>We&rsquo;ll WhatsApp you when it dispatches (5&ndash;7 days), with tracking.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium" style={{ background: "#EEEDFE", color: "#6A0DAD" }}>3</span>
                <span>Your full Sacred Report will land in your inbox shortly.</span>
              </li>
            </ul>
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="ph-cta mt-6 w-full justify-center"
              data-testid="oc-whatsapp-cta"
            >
              Chat on WhatsApp
              <span className="arrow" aria-hidden>&rarr;</span>
            </a>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link to="/" className="ph-btn-ghost" data-testid="oc-back-home">Back to home</Link>
        </div>
      </section>
    </div>
  );
}
